// src/components/SchoolAdmin/SchoolAdminPortal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import drIsInLogo from "../../assets/dr-is-in.png";
import api from "../../api";
import AuraSosPanel from "../AuraSOS/AuraSosPanel";

type AvatarItem = {
  id: string;
  label: string;
  src: string;
  tags?: string[];
};

type SimpleAvatarGalleryModalProps = {
  open: boolean;
  title?: string;
  avatars: AvatarItem[];
  selectedId?: string;
  onSelect: (avatar: AvatarItem) => void;
  onClose: () => void;
  onClear?: () => void;
};

type PublicAvatarsResponse = {
  ok?: boolean;
  version?: string | number;
  count?: number;
  avatars?: Array<{
    id?: string;
    label?: string;
    src?: string;
    tags?: string[];
  }>;
};

type SchoolProfileResp = {
  org_id?: number;
  org_code?: string;
  school_name?: string;
  org_name?: string;
  admin_name?: string;
  contact_name?: string;
  admin_email?: string;
  contact_email?: string;
  admin_phone?: string;
  contact_phone?: string;
};

function SimpleAvatarGalleryModal({
  open,
  title = "Choose an avatar",
  avatars,
  selectedId,
  onSelect,
  onClose,
  onClear,
}: SimpleAvatarGalleryModalProps) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        zIndex: 20000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(980px, 100%)",
          borderRadius: 18,
          background: "rgba(255,255,255,0.96)",
          border: "1px solid rgba(255,255,255,0.65)",
          boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
          padding: 16,
          maxHeight: "86vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 18 }}>{title}</div>

          <div style={{ display: "flex", gap: 10 }}>
            {onClear ? (
              <button
                type="button"
                onClick={onClear}
                style={modalButtonSecondaryStyle}
              >
                Clear
              </button>
            ) : null}

            <button
              type="button"
              onClick={onClose}
              style={modalButtonStyle}
            >
              Close
            </button>
          </div>
        </div>

        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
            gap: 12,
            overflowY: "auto",
            padding: 6,
            flex: 1,
          }}
        >
          {avatars.map((a) => {
            const selected = a.id === selectedId;

            return (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a)}
                title={a.label}
                style={{
                  textAlign: "left",
                  borderRadius: 14,
                  border: selected
                    ? "2px solid rgba(36,198,220,0.9)"
                    : "1px solid rgba(0,0,0,0.10)",
                  background: selected
                    ? "rgba(36,198,220,0.10)"
                    : "rgba(255,255,255,0.7)",
                  padding: 10,
                  cursor: "pointer",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.9)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={a.src}
                    alt={a.label}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.currentTarget.src = drIsInLogo;
                    }}
                  />
                </div>

                <div style={{ marginTop: 8, fontWeight: 900, fontSize: 13 }}>
                  {a.label}
                </div>

                <div
                  style={{
                    marginTop: 2,
                    fontWeight: 800,
                    fontSize: 11,
                    opacity: 0.75,
                  }}
                >
                  {a.tags?.join(", ") || ""}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75, fontWeight: 800 }}>
          Showing {avatars.length} avatar(s)
        </div>
      </div>
    </div>
  );
}

const modalButtonStyle: React.CSSProperties = {
  height: 38,
  padding: "0 12px",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  background: "rgba(255,255,255,0.9)",
  fontWeight: 900,
  cursor: "pointer",
};

const modalButtonSecondaryStyle: React.CSSProperties = {
  height: 38,
  padding: "0 12px",
  borderRadius: 12,
  border: "1px solid rgba(0,0,0,0.12)",
  background: "rgba(0,0,0,0.05)",
  fontWeight: 900,
  cursor: "pointer",
};

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}

const STORES: Storage[] = [sessionStorage, localStorage];

function getFirst(keys: string[]): string {
  for (const store of STORES) {
    for (const k of keys) {
      const v = store.getItem(k);
      if (v && String(v).trim()) return String(v).trim();
    }
  }
  return "";
}

function setBoth(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function removeBoth(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {}
  try {
    localStorage.removeItem(key);
  } catch {}
}

function absolutizeSrc(src: string): string {
  return String(src || "");
}

function firstNonEmpty(...vals: Array<string | null | undefined>) {
  for (const v of vals) {
    const s = String(v || "").trim();
    if (s) return s;
  }
  return "";
}

export default function SchoolAdminPortal() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const token = useMemo(
    () =>
      normalizeBearer(
        getFirst([
          "school_admin_token",
          "school_access_token",
          "school_token",
          "admin_token",
          "access_token",
          "token",
        ])
      ),
    []
  );

  const storedRole = useMemo(() => getFirst(["role"]).toLowerCase(), []);
  const storedOrgCode = useMemo(() => getFirst(["org_code"]), []);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [schoolName, setSchoolName] = useState("");
  const [orgCode, setOrgCode] = useState(storedOrgCode || "");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPhone, setAdminPhone] = useState("");

  const fallbackAvatars: AvatarItem[] = useMemo(
    () => [
      { id: "fallback_01", label: "Avatar 01", src: drIsInLogo, tags: ["fallback"] },
      { id: "fallback_02", label: "Avatar 02", src: drIsInLogo, tags: ["fallback"] },
      { id: "fallback_03", label: "Avatar 03", src: drIsInLogo, tags: ["fallback"] },
    ],
    []
  );

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [pickedAvatar, setPickedAvatar] = useState<AvatarItem | null>(null);
  const [avatarList, setAvatarList] = useState<AvatarItem[]>(fallbackAvatars);

  const logout = () => {
    [
      "school_admin_token",
      "school_access_token",
      "school_token",
      "admin_token",
      "access_token",
      "token",
      "role",
      "org_code",
      "org_id",
      "school_name",
    ].forEach(removeBoth);

    navigate("/");
  };

  async function loadAvatarsFromServer() {
    try {
      const res = await api.get<PublicAvatarsResponse>(`/public/avatars?t=${Date.now()}`, {
        headers: { "Cache-Control": "no-cache" },
      });

      const data = res.data;
      const list = Array.isArray(data?.avatars) ? data.avatars : [];

      if (!list.length) {
        setAvatarList(fallbackAvatars);
        return;
      }

      setAvatarList(
        list.map((a, idx) => ({
          id: String(a.id || `avatar_${idx}`),
          label: String(a.label || a.id || `Avatar ${idx + 1}`),
          src: absolutizeSrc(String(a.src || drIsInLogo)),
          tags: a.tags || [],
        }))
      );
    } catch {
      setAvatarList(fallbackAvatars);
    }
  }

  useEffect(() => {
    void loadAvatarsFromServer();
  }, [fallbackAvatars]);

  useEffect(() => {
    let alive = true;

    async function loadPortal() {
      setLoading(true);
      setErr("");
      setMsg("");

      if (!token) {
        if (alive) {
          setErr("Missing school admin token. Please log in again.");
          setLoading(false);
        }
        return;
      }

      try {
        const profileRes = await api.get<SchoolProfileResp>("/school/profile");

        if (!alive) return;

        const p = profileRes.data || {};

        const nextSchoolName = firstNonEmpty(p.school_name, p.org_name);
        const nextOrgCode = firstNonEmpty(p.org_code, storedOrgCode);
        const nextAdminName = firstNonEmpty(p.admin_name, p.contact_name);
        const nextAdminEmail = firstNonEmpty(p.admin_email, p.contact_email);
        const nextAdminPhone = firstNonEmpty(p.admin_phone, p.contact_phone);

        setSchoolName(nextSchoolName);
        setOrgCode(nextOrgCode);
        setAdminName(nextAdminName);
        setAdminEmail(nextAdminEmail);
        setAdminPhone(nextAdminPhone);

        if (nextOrgCode) setBoth("org_code", nextOrgCode);
        if (nextSchoolName) setBoth("school_name", nextSchoolName);
        if (!storedRole) setBoth("role", "school_admin");

        setMsg("Ready ✅");
      } catch (e: any) {
        if (!alive) return;

        const status = Number(e?.response?.status || 0);
        const detail = String(e?.response?.data?.detail || e?.message || "").trim();

        if (status === 401) {
          setErr("Your school admin session has expired. Please log in again.");
          setTimeout(() => {
            navigate("/school-registration");
          }, 800);
        } else if (status === 404) {
          setErr("School profile route is not available yet. Portal loaded in limited mode.");
          setMsg("Limited mode ✅");
        } else {
          setErr(detail || "Failed to load school admin portal.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    }

    void loadPortal();

    return () => {
      alive = false;
    };
  }, [token, navigate, storedOrgCode, storedRole]);

  const statusText = loading
    ? "Loading portal…"
    : err
    ? err
    : msg || "Ready ✅";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #24c6dc 0%, #514a9d 100%)",
        padding: 22,
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <AuraSosPanel
          role="school"
          orgCode={orgCode}
          responderId={orgCode}
          canClose
          title="🚨 AURA SOS™ EMERGENCY NOTICE — School Response"
        />
        <div
          style={{
            background: "rgba(255,255,255,0.90)",
            border: "1px solid rgba(255,255,255,0.6)",
            borderRadius: 16,
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 14,
                overflow: "hidden",
                background: "rgba(255,255,255,0.9)",
                border: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={drIsInLogo}
                alt="Doctor Is In"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>

            <div>
              <div style={{ fontWeight: 900, fontSize: 16, lineHeight: 1.1 }}>
                School Admin Portal
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                Doctor AI with a human touch.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            style={{
              height: 40,
              padding: "0 14px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.95)",
              cursor: "pointer",
              fontWeight: 900,
            }}
          >
            Logout
          </button>
        </div>

        <div
          style={{
            marginTop: 14,
            background: "rgba(255,255,255,0.90)",
            border: "1px solid rgba(255,255,255,0.6)",
            borderRadius: 16,
            padding: 16,
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ margin: "0 0 6px", fontSize: 28, fontWeight: 900 }}>
            School Dashboard
          </h2>

          <div style={{ opacity: 0.92, lineHeight: 1.6, fontWeight: 700 }}>
            School: <b>{schoolName || "—"}</b>
            <br />
            Org Code: <b>{orgCode || "—"}</b>
            <br />
            Administrator: <b>{adminName || "—"}</b>
            <br />
            Email: <b>{adminEmail || "—"}</b>
            <br />
            Phone: <b>{adminPhone || "—"}</b>
            <br />
            <span style={{ color: err ? "#b00020" : "#146c2e", fontWeight: 900 }}>
              {statusText}
            </span>
          </div>

          <div
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "minmax(360px, 520px) 1fr",
              gap: 18,
            }}
          >
            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.65)",
                padding: 18,
                boxShadow: "0 12px 34px rgba(0,0,0,0.10)",
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 16 }}>Avatar Tool</div>

              <div style={{ marginTop: 8, opacity: 0.8, fontWeight: 700 }}>
                Uses the published backend list: <b>/public/avatars</b>
              </div>

              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12 }}>
                <div
                  style={{
                    width: 84,
                    height: 84,
                    borderRadius: 18,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.10)",
                    background: "rgba(255,255,255,0.85)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={pickedAvatar?.src || drIsInLogo}
                    alt="Picked avatar"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => {
                      e.currentTarget.src = drIsInLogo;
                    }}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <button
                    type="button"
                    onClick={() => {
                      void loadAvatarsFromServer();
                      setGalleryOpen(true);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: "1px solid rgba(0,0,0,0.15)",
                      background: "rgba(255,255,255,0.95)",
                      cursor: "pointer",
                      fontWeight: 900,
                    }}
                  >
                    Open Avatar Gallery
                  </button>

                  {pickedAvatar ? (
                    <button
                      type="button"
                      onClick={() => setPickedAvatar(null)}
                      style={{
                        marginTop: 10,
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: "1px solid rgba(0,0,0,0.10)",
                        background: "rgba(0,0,0,0.05)",
                        cursor: "pointer",
                        fontWeight: 900,
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                borderRadius: 18,
                border: "1px solid rgba(255,255,255,0.65)",
                padding: 18,
                boxShadow: "0 12px 34px rgba(0,0,0,0.10)",
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 16 }}>School Admin Actions</div>

              <div style={{ marginTop: 10, fontWeight: 800, opacity: 0.8, lineHeight: 1.6 }}>
                Plug your real school admin tools here:
                <br />
                roster uploads
                <br />
                school profile
                <br />
                wellness reports
                <br />
                exports and approvals
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: "center",
            marginTop: 14,
            fontSize: 12,
            opacity: 0.85,
            color: "#fff",
          }}
        >
          © {year} Doctor Is In App. All rights reserved.
        </div>
      </div>

      <SimpleAvatarGalleryModal
        open={galleryOpen}
        title="Choose an avatar"
        avatars={avatarList}
        selectedId={pickedAvatar?.id || ""}
        onSelect={(a) => {
          setPickedAvatar(a);
          setGalleryOpen(false);
        }}
        onClose={() => setGalleryOpen(false)}
        onClear={() => setPickedAvatar(null)}
      />
    </div>
  );
}